import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 1: PRIYA
 * Style: Fresh Minimal
 * Best for: Modern Indian fashion, Ethnic wear, Sarees, Lehengas
 * Clean white with sage green accents
 */
const Template1 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const [email, setEmail] = useState('');
    const touchStartX = useRef(0);

    // Categories
    const categories = [
        {
            name: 'Sarees',
            description: 'Timeless drapes for every occasion',
            image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=600'
        },
        {
            name: 'Lehengas',
            description: 'Regal ensembles for celebrations',
            image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=600'
        },
        {
            name: 'Kurtas',
            description: 'Everyday elegance in every stitch',
            image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=600'
        },
        {
            name: 'Accessories',
            description: 'The finishing touch of beauty',
            image: 'https://images.pexels.com/photos/2118563/pexels-photo-2118563.jpeg?auto=compress&cs=tinysrgb&w=600'
        }
    ];

    // Products
    const products = [
        { id: 1, name: 'Chanderi Silk Saree', price: '\u20B98,999', image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Banarasi Brocade Lehenga', price: '\u20B924,999', image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Embroidered Anarkali Kurta', price: '\u20B95,499', image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Kundan Jewellery Set', price: '\u20B93,299', image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Organza Printed Saree', price: '\u20B96,799', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Velvet Bridal Lehenga', price: '\u20B932,999', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Cotton Chikankari Kurta', price: '\u20B92,999', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Oxidised Silver Jhumkas', price: '\u20B91,499', image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400' }
    ];

    // Testimonials
    const testimonials = [
        {
            text: 'The saree I ordered was absolutely stunning. The fabric quality is exceptional, and the colors are exactly as shown. I received so many compliments at my sister\'s wedding!',
            name: 'Ananya Sharma',
            city: 'Mumbai',
            rating: 5
        },
        {
            text: 'PRIYA has become my go-to for ethnic wear. The kurtas are so comfortable yet elegant. Their attention to detail in packaging made it feel like a gift to myself.',
            name: 'Meera Patel',
            city: 'Ahmedabad',
            rating: 5
        },
        {
            text: 'I was hesitant about buying a lehenga online, but the fit was perfect. The customer service team was incredibly helpful with sizing. Truly a wonderful experience.',
            name: 'Kavitha Reddy',
            city: 'Hyderabad',
            rating: 5
        }
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [testimonials.length]);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) =>
                diff > 0
                    ? (prev + 1) % testimonials.length
                    : (prev - 1 + testimonials.length) % testimonials.length
            );
        }
    };

    const toggleWishlist = (e, id) => {
        e.stopPropagation();
        setWishlist((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleNewsletterSubmit = (e) => {
        e.preventDefault();
        if (email.trim()) {
            setEmail('');
        }
    };

    return (
        <div style={{ fontFamily: "'Poppins', sans-serif" }} className="w-full min-h-screen bg-white">
            <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            {/* ===== SECTION 1: HERO ===== */}
            <section className="relative w-full min-h-[85vh] md:min-h-screen overflow-hidden">
                {/* Background Image */}
                <img
                    src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=1200"
                    alt="Indian woman in elegant saree"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />

                {/* Hero Content */}
                <div className="relative z-10 h-full min-h-[85vh] md:min-h-screen flex items-center">
                    <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 w-full">
                        <div className="max-w-xl">
                            <p className="text-xs md:text-sm tracking-[0.3em] uppercase mb-3 md:mb-4 text-white/70 font-light">
                                New Collection 2026
                            </p>
                            <h1 className="text-3xl md:text-5xl lg:text-6xl font-semibold text-white mb-4 md:mb-6 leading-tight">
                                Discover Timeless Elegance
                            </h1>
                            <p className="text-sm md:text-base lg:text-lg text-white/80 mb-6 md:mb-8 max-w-md leading-relaxed font-light">
                                Handcrafted Indian ethnic wear that celebrates tradition with a modern touch. Each piece tells a story of artisanal excellence.
                            </p>
                            <button
                                onClick={() => navigate('/template1/shop')}
                                className="inline-flex items-center gap-3 bg-[#2D6A4F] text-white px-6 py-3 md:px-8 md:py-3.5 text-sm font-medium tracking-wide hover:bg-[#1B4332] transition-all duration-300 active:scale-[0.98]"
                            >
                                Shop Collection <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 2: CATEGORIES ===== */}
            <section className="py-12 md:py-20 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
                    <div className="text-center mb-8 md:mb-14">
                        <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#2D6A4F] mb-2 font-medium">Collections</p>
                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-[#1A1A1A]">
                            Shop by Category
                        </h2>
                    </div>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5 lg:gap-6">
                        {categories.map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template1/shop')}
                                className="relative aspect-[3/4] overflow-hidden cursor-pointer group"
                            >
                                <img
                                    src={category.image}
                                    alt={category.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                                    <h3 className="text-base md:text-lg font-semibold text-white mb-1">
                                        {category.name}
                                    </h3>
                                    <p className="text-xs md:text-sm text-white/70 hidden md:block">
                                        {category.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: FEATURED PRODUCTS ===== */}
            <section className="py-12 md:py-20 lg:py-24 bg-[#F8FAF8]">
                <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
                    <div className="flex items-center justify-between mb-8 md:mb-14">
                        <div>
                            <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#2D6A4F] mb-2 font-medium">Curated for You</p>
                            <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-[#1A1A1A]">
                                Featured Products
                            </h2>
                        </div>
                        <button
                            onClick={() => navigate('/template1/shop')}
                            className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-[#2D6A4F] hover:text-[#1B4332] transition-colors"
                        >
                            View All <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-4 lg:gap-6">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                className="group cursor-pointer"
                                onClick={() => navigate(`/template1/product/${product.id}`)}
                            >
                                <div className="relative overflow-hidden bg-white mb-2 md:mb-3" style={{ paddingBottom: '133.33%' }}>
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-2 right-2 md:top-3 md:right-3 w-8 h-8 md:w-9 md:h-9 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                    >
                                        <Heart
                                            size={15}
                                            className={wishlist[product.id] ? 'fill-red-500 text-red-500' : 'text-[#1A1A1A]'}
                                        />
                                    </button>
                                </div>
                                <h3 className="text-xs md:text-sm font-medium text-[#1A1A1A] mb-0.5 line-clamp-1">
                                    {product.name}
                                </h3>
                                <p className="text-sm md:text-base font-semibold text-[#2D6A4F]">{product.price}</p>
                            </div>
                        ))}
                    </div>
                    {/* Mobile: View All */}
                    <div className="mt-8 text-center md:hidden">
                        <button
                            onClick={() => navigate('/template1/shop')}
                            className="inline-flex items-center gap-2 text-sm font-medium text-[#2D6A4F] hover:text-[#1B4332] transition-colors"
                        >
                            View All Products <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: TESTIMONIALS ===== */}
            <section className="py-12 md:py-20 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
                    <div className="text-center mb-8 md:mb-14">
                        <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#2D6A4F] mb-2 font-medium">Testimonials</p>
                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-[#1A1A1A]">
                            What Our Customers Say
                        </h2>
                    </div>
                    <div
                        className="max-w-2xl mx-auto"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div className="text-center px-4">
                            {/* Stars */}
                            <div className="flex justify-center gap-1 mb-4 md:mb-6">
                                {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                                    <Star key={i} size={16} className="fill-[#2D6A4F] text-[#2D6A4F]" />
                                ))}
                            </div>
                            {/* Quote */}
                            <p className="text-sm md:text-base lg:text-lg text-[#6B7280] leading-relaxed mb-6 md:mb-8 italic">
                                "{testimonials[activeTestimonial].text}"
                            </p>
                            {/* Name */}
                            <p className="text-sm md:text-base font-semibold text-[#1A1A1A]">
                                {testimonials[activeTestimonial].name}
                            </p>
                            <p className="text-xs md:text-sm text-[#9CA3AF]">
                                {testimonials[activeTestimonial].city}
                            </p>
                        </div>

                        {/* Dots */}
                        <div className="flex justify-center gap-2 mt-6 md:mt-8">
                            {testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setActiveTestimonial(index)}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                        index === activeTestimonial ? 'bg-[#2D6A4F] w-6' : 'bg-[#D1D5DB]'
                                    }`}
                                />
                            ))}
                        </div>

                        {/* Arrow Nav (Desktop) */}
                        <div className="hidden md:flex justify-center gap-4 mt-6">
                            <button
                                onClick={() => setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                                className="w-10 h-10 border border-[#E5E7EB] rounded-full flex items-center justify-center hover:border-[#2D6A4F] hover:text-[#2D6A4F] transition-colors"
                            >
                                <ChevronLeft size={18} />
                            </button>
                            <button
                                onClick={() => setActiveTestimonial((prev) => (prev + 1) % testimonials.length)}
                                className="w-10 h-10 border border-[#E5E7EB] rounded-full flex items-center justify-center hover:border-[#2D6A4F] hover:text-[#2D6A4F] transition-colors"
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: NEWSLETTER ===== */}
            <section className="py-12 md:py-20 lg:py-24 bg-[#F8FAF8]">
                <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
                    <div className="max-w-xl mx-auto text-center">
                        <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#2D6A4F] mb-2 font-medium">Newsletter</p>
                        <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-3 md:mb-4">
                            Join the PRIYA Family
                        </h2>
                        <p className="text-sm md:text-base text-[#6B7280] mb-6 md:mb-8">
                            Be the first to know about new collections, exclusive offers, and styling tips delivered straight to your inbox.
                        </p>
                        <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                className="flex-1 px-4 py-3 border border-[#E5E7EB] bg-white text-sm text-[#1A1A1A] placeholder-[#9CA3AF] focus:outline-none focus:border-[#2D6A4F] transition-colors"
                                required
                            />
                            <button
                                type="submit"
                                className="px-6 py-3 bg-[#2D6A4F] text-white text-sm font-medium hover:bg-[#1B4332] transition-colors"
                            >
                                Subscribe
                            </button>
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Template1;
